import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _styled from '@emotion/styled-base';
import React__default, { useState, useRef, useEffect } from 'react';
import useCombinedRefs from '../../utils/use-combined-refs.js';
import { InputGroup } from '@blueprintjs/core';
import { Search32, Close32 } from '@carbon/icons-react';
import { backgroundDisabled } from '../theme/theme.js';
import { IconOnlyButton } from '../icon-only-button/icon-only-button.js';
import { Button } from '../button/button.js';

var _excluded = ["alignRight", "maxWidth", "disabled", "value", "onClear", "searchTooltipText"];

var ExpandableSearchWrapper = /*#__PURE__*/_styled("div", {
  target: "e15ntjsw0"
})("width:100%;margin-left:", function (_ref) {
  var alignRight = _ref.alignRight;
  return alignRight ? "auto" : "0";
}, ";transition-property:max-width;transition-duration:300ms;&.open{max-width:", function (_ref2) {
  var maxWidth = _ref2.maxWidth;
  return maxWidth ? "".concat(maxWidth, "px") : "100%";
}, ";}&.closed{max-width:2rem;}.vds-search-button{width:2rem;height:2rem;margin:0;transition:width 300ms,height 300ms,margin 300ms;}&.open .vds-search-button{width:1.5rem;height:1.5rem;margin:0.25rem;}> .bp3-input-group{margin-left:", function (_ref3) {
  var alignRight = _ref3.alignRight;
  return alignRight ? "auto" : "0";
}, ";}");

var ExpandableSearchInputGroup = /*#__PURE__*/_styled(InputGroup, {
  target: "e15ntjsw1"
})("transition-property:width;transition-duration:300ms;.vds-toggle-button{width:2rem;height:2rem;margin:0;transition-property:transform;transition-duration:300ms;.bp3-icon{transition-property:transform;transition-duration:300ms;}}.vds-clear-button{width:1.5rem;height:1.5rem;transition-property:opacity;transition-duration:150ms;}.bp3-input{transition-property:padding,background-color,box-shadow;transition-duration:150ms;}&.bp3-large{.vds-toggle-button{width:2.5rem;height:2.5rem;margin:0;}.vds-clear-button{width:2rem;height:2rem;}@media all and (-ms-high-contrast:none),(-ms-high-contrast:active){.bp3-input{padding-top:0;padding-bottom:0;}}}.bp3-input:-ms-input-placeholder{transition-property:opacity;transition-duration:150ms;}.bp3-input::-ms-input-placeholder{transition-property:opacity;transition-duration:150ms;}.bp3-input::placeholder{transition-property:opacity;transition-duration:150ms;}.open &{width:100%;.vds-toggle-button{transform:scale(0.75);.bp3-icon{transform:scale(1.35);}}.vds-clear-button{opacity:1;transition-delay:150ms;}.bp3-input{background-color:white;&:disabled{background-color:", backgroundDisabled, ";}}.bp3-input:-ms-input-placeholder{opacity:1;transition-delay:150ms;}.bp3-input::-ms-input-placeholder{opacity:1;transition-delay:150ms;}.bp3-input::placeholder{opacity:1;transition-delay:150ms;}}.closed &{width:2rem;.vds-clear-button{opacity:0;}.bp3-input{padding-right:0 !important;background-color:transparent;box-shadow:inset 0 0 0 1px transparent !important;transition-delay:0ms,150ms,150ms;}.bp3-input:-ms-input-placeholder{opacity:0;}.bp3-input::-ms-input-placeholder{opacity:0;}.bp3-input::placeholder{opacity:0;}}");

var ExpandableSearch = /*#__PURE__*/React__default.forwardRef(function (_ref4, ref) {
  var alignRight = _ref4.alignRight,
      maxWidth = _ref4.maxWidth,
      disabled = _ref4.disabled,
      value = _ref4.value,
      onClear = _ref4.onClear,
      _ref4$searchTooltipTe = _ref4.searchTooltipText,
      searchTooltipText = _ref4$searchTooltipTe === void 0 ? "Search" : _ref4$searchTooltipTe,
      props = _objectWithoutProperties(_ref4, _excluded);

  var isControlled = value !== undefined;

  var _useState = useState(!!value),
      _useState2 = _slicedToArray(_useState, 2),
      toggleState = _useState2[0],
      setToggleState = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      showTooltip = _useState4[0],
      setShowTooltip = _useState4[1];

  var _useState5 = useState(value || ""),
      _useState6 = _slicedToArray(_useState5, 2),
      searchValue = _useState6[0],
      setSearchValue = _useState6[1];

  var inputRef = useRef(null);

  var _buttonRef = useRef(null);

  var combinedRef = useCombinedRefs(inputRef, ref);

  var handleKeyPress = function handleKeyPress(e) {
    var key = e.key;

    if (key === "Escape") {
      var _buttonRef$current;

      setToggleState(false);
      (_buttonRef$current = _buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.focus();
    }
  };

  useEffect(function () {
    /**
     * This effect checks if user is still editing the input.
     * If still editing, do nothing, if lost focus, and value
     * is set to '' in controlled mode, close search.
     */
    if (isControlled && toggleState && value === "" && inputRef.current !== document.activeElement) {
      setToggleState(false);
    }
  }, [value]);
  return /*#__PURE__*/React__default.createElement(ExpandableSearchWrapper, {
    alignRight: alignRight,
    "aria-expanded": toggleState ? "true" : "false",
    className: toggleState ? "open" : "closed",
    maxWidth: maxWidth
  }, /*#__PURE__*/React__default.createElement(ExpandableSearchInputGroup, _extends({
    disabled: disabled,
    inputRef: combinedRef,
    leftElement: /*#__PURE__*/React__default.createElement(IconOnlyButton, {
      className: "vds-search-button",
      buttonRef: function buttonRef(node) {
        return _buttonRef.current = node;
      },
      minimal: true,
      icon: Search32,
      text: searchTooltipText,
      tooltipProps: {
        open: showTooltip,
        onOpenChange: function onOpenChange(newOpen) {
          return setShowTooltip(toggleState ? false : newOpen);
        }
      },
      onClick: function onClick() {
        var _inputRef$current;

        setToggleState(true);
        (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
      }
    }),
    rightElement: /*#__PURE__*/React__default.createElement(Button, {
      className: "vds-clear-button",
      minimal: true,
      disabled: !toggleState || disabled,
      icon: Close32,
      onClick: function onClick() {
        var _inputRef$current2;

        setSearchValue("");
        (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.focus();
        onClear === null || onClear === void 0 ? void 0 : onClear();
      }
    }),
    tabIndex: toggleState ? undefined : -1
  }, props, {
    onKeyUp: function onKeyUp(e) {
      handleKeyPress(e);
    },
    onChange: function onChange(e) {
      if (props.onChange) {
        props.onChange(e);
      }

      setSearchValue(e.target.value);
    },
    onBlur: function onBlur(e) {
      // Close if blurred to anything but the search button, and the search field is blank.
      if (e.relatedTarget === _buttonRef.current) return;

      if (value === "" || searchValue === "") {
        var _buttonRef$current2;

        setToggleState(false);
        (_buttonRef$current2 = _buttonRef.current) === null || _buttonRef$current2 === void 0 ? void 0 : _buttonRef$current2.focus();
      }
    },
    value: isControlled ? value : searchValue
  })));
});

export { ExpandableSearch };
//# sourceMappingURL=expandable-search.js.map
